import { Box, Typography, MenuItem, InputLabel } from "@mui/material";
import Grid from "@mui/material/Grid";
import FileUpload from "../../../../../../components/FileUpload";
import { useState } from "react";
import Input from "../../../../../../components/Input";
import theme from "../../../../../../theme";
import { string } from "../../../../../../constants/string";
import { useClaimFormStore } from "../../../../../../store/useClaimFormStore";
import ButtonComp from "../../../../../../components/Button";
import { useBankNames } from "../../../../../../hooks/useBankNames";
import { useBankFormStore } from "../../../../../../store/useBankFormStore";
import { isValidContactNumber } from "../../../../../../utils/validators";

export default function BankingInfo({
  handleBankingInfoNext,
  handleBack,
}: {
  handleBankingInfoNext: (data: any) => void;
  handleBack: () => void;
  handleTenPercentBankingInfoNext?: ((data: any) => void) | undefined;
}) {
  const accountTypeMap: Record<string, any> = {
    "cheque account": 1,
    "savings account": 2,
    "transmission account": 3,
  };

  const [errors, setErrors] = useState<{ [key: string]: string }>({
    payableTo: "",
    relationship: "",
    accountHolder: "",
    bankName: "",
    accountNumber: "",
    branch: "",
    branchCode: "",
    accountType: "",
    passportNumber: "",
    sourceOfFunds: "",
    occupation: "",
    emailAddress: "",
    nationality: "",
    countryOfResidence: "",
    countryOfBirth: "",
    signature_document: "",
    bank_statement: "",
    cellPhoneNumber: "",
    accountName: "",
  });
  const [showErrors, setShowErrors] = useState(false);

  const { documents } = useClaimFormStore();
  
  const signatureFile = documents["signature_document"];
  const bankFile = documents["bank_statement"];
  const signatureFileName = signatureFile
    ? signatureFile.name
    : "No file chosen";
  const bankFileName = bankFile ? bankFile.name : "No file chosen";
  const { bankingInfo, setBankingInfo } = useBankFormStore();
  const bankDetails = bankingInfo;

  const { bankOptions } = useBankNames();

  const addDocument = useClaimFormStore((state) => state.addDocument);

  const handleChange = (field: string, value: string) => {
      if (field === "accountType") {
          setBankingInfo({
            accountType: value,
            accountTypeId: accountTypeMap[value] || "",
          });
        } else {
          setBankingInfo({ [field]: value });
        }

    if (showErrors) {
      setErrors((prev) => {
        const newErrors = { ...prev };

        if (!value) {
          newErrors[field] = `Please enter ${field
            .replace(/([A-Z])/g, " $1")
            .toLowerCase()}`;
        } else {
          newErrors[field] = "";
        }

        if (field === "emailAddress") {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (value && !emailRegex.test(value)) {
            newErrors.emailAddress = "Invalid email address";
          }
        }

        if (field === "cellPhoneNumber" && typeof value === "string") {
          newErrors.cellPhoneNumber = !isValidContactNumber(value.trim())
            ? "Invalid contact number (10 digits only)"
            : "";
        }

        return newErrors;
      });
    }
    if (showErrors) {
      setErrors((prev) => {
        const newErrors = { ...prev };

        if (!value) {
          newErrors[field] = `Please enter ${field
            .replace(/([A-Z])/g, " $1")
            .toLowerCase()}`;
        } else {
          newErrors[field] = "";
        }

        if (field === "emailAddress") {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (value && !emailRegex.test(value)) {
            newErrors.emailAddress = "Invalid email address";
          }
        }

        if (field === "cellPhoneNumber" && typeof value === "string") {
          newErrors.cellPhoneNumber = !isValidContactNumber(value.trim())
            ? "Invalid contact number (10 digits only)"
            : "";
        }

        return newErrors;
      });
    }
  };

  const validateBankForm = () => {
    const fieldsToValidate = [
      "bankName",
      "accountNumber",
      "branchCode",
      "accountType",
      "signature_document",
      "bank_statement",
      "cellPhoneNumber",
      "accountName",
    ];

    const newErrors: any = {};
    let isValid = true;

    fieldsToValidate.forEach((field) => {
      const value = bankDetails[field as keyof typeof bankDetails];
      if (!value) {
        newErrors[field] = `Please enter ${field
          .replace(/([A-Z])/g, " $1")
          .toLowerCase()}`;
        isValid = false;
      }
    });

    // Email format check
    if (
      bankDetails.emailAddress &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bankDetails.emailAddress)
    ) {
      newErrors.emailAddress = "Invalid email address";
      isValid = false;
    }

    if (!documents.signature_document) {
      newErrors.signature_document = "Upload a document";
      isValid = false;
    }
    if (!documents.bank_statement) {
      newErrors.bank_statement = "Upload a document";
      isValid = false;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  };

  return (
    <>
      <Box component="form" noValidate autoComplete="off" mt={6}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Input
              fullWidth
              type="number"
              label="Contact Number"
              variant="outlined"
              value={bankDetails.cellPhoneNumber}
              onChange={(e: any) =>
                handleChange("cellPhoneNumber", e.target.value)
              }
              error={showErrors && Boolean(errors.cellPhoneNumber)}
              helperText={showErrors ? errors.cellPhoneNumber : ""}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Input
              select
              fullWidth
              label={string.bankingInfo.nameOfBank}
              variant="outlined"
              value={bankDetails.bankName}
              onChange={(e: any) => handleChange("bankName", e.target.value)}
              error={showErrors && Boolean(errors.bankName)}
              helperText={showErrors ? errors.bankName : ""}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: any) =>
                  selected ? (
                    selected
                  ) : (
                    <span style={{ color: "#aaa" }}>Select Bank Name</span>
                  ),
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      maxHeight: "300px",
                    },
                  },
                },
              }}
            >
              {bankOptions.map((bank) => (
                <MenuItem
                  key={bank}
                  value={bank}
                  sx={{
                    background: "background.default",
                    borderBottom: ` 1px solid ${theme.palette.grey[200]}`,
                  }}
                >
                  {bank}
                </MenuItem>
              ))}
            </Input>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Input
              fullWidth
              label={string.bankingInfo.accountNumber}
              variant="outlined"
              value={bankDetails.accountNumber}
              onChange={(e: any) =>
                handleChange("accountNumber", e.target.value)
              }
              error={showErrors && Boolean(errors.accountNumber)}
              helperText={showErrors ? errors.accountNumber : ""}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Input
              fullWidth
              label="Account Name"
              variant="outlined"
              value={bankDetails.accountName}
              onChange={(e: any) => handleChange("accountName", e.target.value)}
              error={showErrors && Boolean(errors.accountName)}
              helperText={showErrors ? errors.accountName : ""}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Input
              fullWidth
              label={string.bankingInfo.branchCode}
              variant="outlined"
              value={bankDetails.branchCode}
              onChange={(e: any) => handleChange("branchCode", e.target.value)}
              error={showErrors && Boolean(errors.branchCode)}
              helperText={showErrors ? errors.branchCode : ""}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Input
              select
              fullWidth
              label={string.bankingInfo.accountType}
              variant="outlined"
              value={bankDetails.accountType}
              onChange={(e: any) => handleChange("accountType", e.target.value)}
              error={showErrors && Boolean(errors.accountType)}
              helperText={showErrors ? errors.accountType : ""}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: any) =>
                  selected ? (
                    selected
                  ) : (
                    <span style={{ color: "#aaa" }}>Select Account Type</span>
                  ),
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      maxHeight: "300px",
                    },
                  },
                },
              }}
            >
              <MenuItem
                value="cheque account"
                sx={{
                  background: "background.default",
                  borderBottom: "1px solid #DFDDDD",
                }}
              >
                Cheque Account
              </MenuItem>
              <MenuItem
                value="transmission account"
                sx={{
                  background: "background.default",
                  borderBottom: "1px solid #DFDDDD",
                }}
              >
                Transmission Account
              </MenuItem>
              <MenuItem
                value="savings account"
                sx={{ background: "background.default" }}
              >
                Savings Account
              </MenuItem>
            </Input>
          </Grid>
        </Grid>
        <Grid container mt={3}>
          <Grid size={{ xs: 12, md: 4 }} textAlign={"left"}>
            <InputLabel sx={{ fontWeight: 600, mb: 1, fontSize: 18 }}>
              Signature
            </InputLabel>
            <FileUpload
              inputId="signature-upload"
              fileName={signatureFileName}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  if (file.size > 2 * 1024 * 1024) {
                    setErrors((prev) => ({
                      ...prev,
                      signature_document: "File must be less than 2MB",
                    }));
                    return;
                  }

                  addDocument("signature_document", file);
                  handleChange("signature_document", file.name);
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.signature_document;
                    return newErrors;
                  });
                }
              }}
            />
            {errors.signature_document && (
              <Typography
                sx={{
                  color: "error.main",
                  fontSize: "0.8rem",
                  textAlign: "left",
                }}
              >
                {errors.signature_document}
              </Typography>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} textAlign={"left"}>
            <InputLabel sx={{ fontWeight: 600, mb: 1, fontSize: 18 }}>
              {string.bankingInfo.proofOfBanking}
            </InputLabel>
            <FileUpload
              inputId="bank-proof"
              fileName={bankFileName}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  if (file.size > 2 * 1024 * 1024) {
                    setErrors((prev) => ({
                      ...prev,
                      bank_statement: "File must be less than 2MB",
                    }));
                    return;
                  }
                  addDocument("bank_statement", file);
                  handleChange("bank_statement", file.name);
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.bank_statement;
                    return newErrors;
                  });
                }
              }}
            />
            {errors.bank_statement && (
              <Typography
                sx={{
                  color: "error.main",
                  fontSize: "0.8rem",
                  textAlign: "left",
                }}
              >
                {errors.bank_statement}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          py: 4,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2 },
        }}
      >
        <ButtonComp
          variant="outlined"
          height={40}
          fontSize="16px"
          width={200}
          onClick={handleBack}
        >
          {string.claims.back}
        </ButtonComp>
        <ButtonComp
          variant="contained"
          height={40}
          fontSize="16px"
          width={200}
          onClick={() => {
            setShowErrors(true);
            if (validateBankForm()) {
              handleBankingInfoNext(bankDetails);
            }
          }}
        >
          {string.claims.submit}
        </ButtonComp>
      </Box>
    </>
  );
}
